import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from './disposerBag'

export interface PbrTextureSetOptions {
  name: string
  colorUrl: string
  normalUrl?: string
  /** Plain grayscale roughness map (not an ORM-packed texture). */
  roughnessUrl?: string
  aoUrl?: string
  /** How many times the map repeats across the surface — tune per mesh size. */
  uScale?: number
  vScale?: number
  metallic?: number
  /** Scales how strongly the scene's HDRI/env texture reflects off this surface — defaults to
   * full (1), but indoor materials usually want this dialed down so a bright outdoor sun/sky
   * doesn't mirror onto them as a hot, unrealistic glint. */
  environmentIntensity?: number
}

function tiled(scene: BABYLON.Scene, bag: DisposerBag, url: string, uScale: number, vScale: number) {
  const tex = bag.track(new BABYLON.Texture(url, scene))
  tex.uScale = uScale
  tex.vScale = vScale
  return tex
}

/**
 * Wraps a real ambientCG-style Color/NormalGL/Roughness/AO set into a
 * PBRMaterial. Roughness rides in `metallicTexture`'s green channel (the
 * standard Babylon trick for a lone grayscale roughness map without a
 * metalness/ORM pack) so ambientCG's "no login, just JPGs" sets work without
 * repacking channels by hand.
 */
export function buildPbrMaterialFromSet(scene: BABYLON.Scene, bag: DisposerBag, opts: PbrTextureSetOptions): BABYLON.PBRMaterial {
  const { uScale = 1, vScale = 1 } = opts
  const mat = bag.track(new BABYLON.PBRMaterial(opts.name, scene))
  mat.albedoTexture = tiled(scene, bag, opts.colorUrl, uScale, vScale)

  if (opts.normalUrl) {
    mat.bumpTexture = tiled(scene, bag, opts.normalUrl, uScale, vScale)
  }

  if (opts.roughnessUrl) {
    mat.metallicTexture = tiled(scene, bag, opts.roughnessUrl, uScale, vScale)
    mat.useRoughnessFromMetallicTextureAlpha = false
    mat.useRoughnessFromMetallicTextureGreen = true
    mat.useMetallnessFromMetallicTextureBlue = false
  }
  mat.metallic = opts.metallic ?? 0
  mat.environmentIntensity = opts.environmentIntensity ?? 1

  if (opts.aoUrl) {
    mat.ambientTexture = tiled(scene, bag, opts.aoUrl, uScale, vScale)
  }

  return mat
}
